import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { InvoiceListItem, InvoiceListParams, PaginatedResponse } from './types';

/**
 * Calculates start_date and end_date in ISO (YYYY-MM-DD) format based on client-side range.
 * Supports monthOffset for full calendar month navigation (e.g. 0 = current month, -1 = prev month).
 */
export function getISODateRange(
  range: 'Recent' | 'MTD' | 'Month' | 'YTD' | 'YoY' | 'Custom',
  monthOffset = 0
): { start_date?: string; end_date?: string } {
  const now = new Date();
  const formatISO = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  switch (range) {
    case 'Recent': {
      const start = new Date(now);
      start.setDate(now.getDate() - 14);
      return { start_date: formatISO(start), end_date: formatISO(now) };
    }
    case 'MTD': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start_date: formatISO(start), end_date: formatISO(now) };
    }
    case 'Month': {
      const targetMonthDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
      const start = new Date(targetMonthDate.getFullYear(), targetMonthDate.getMonth(), 1);
      const end = new Date(targetMonthDate.getFullYear(), targetMonthDate.getMonth() + 1, 0);
      return { start_date: formatISO(start), end_date: formatISO(end) };
    }
    case 'YTD': {
      const start = new Date(now.getFullYear(), 0, 1);
      return { start_date: formatISO(start), end_date: formatISO(now) };
    }
    case 'YoY': {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      return { start_date: formatISO(start), end_date: formatISO(now) };
    }
    case 'Custom':
    default:
      return {};
  }
}

/**
 * TanStack Query hook to list invoices from GET /api/v2/invoices/
 */
export function useInvoices(params: InvoiceListParams = {}) {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== '' && value !== null
    )
  );

  return useQuery<PaginatedResponse<InvoiceListItem>>({
    queryKey: ['invoices', cleanParams],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<InvoiceListItem> | InvoiceListItem[]>(
        '/v2/invoices/',
        { params: cleanParams }
      );
      if (Array.isArray(response.data)) {
        return {
          results: response.data,
          pagination: {
            page: 1,
            page_size: response.data.length,
            total: response.data.length,
            has_next: false,
          },
        };
      }
      return response.data;
    },
    staleTime: 30_000,
  });
}
